import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'primevue/usetoast';
import dataService from '@/services/dataService';
import { syncService } from '@/services/syncService';

export function useTourneeData() {
  const store = useStore();
  const toast = useToast();
  const loading = ref(true);
  const loadError = ref(null);
  const pendingUpdatesCount = ref(0);
  const hasManualSyncNeeded = ref(false);

  // Propriétés calculées à partir du store
  const tourneeData = computed(() => store.state.tourneeData);
  const isOnline = computed(() => store.getters['sync/isOnline']);
  const isUserOfflineMode = computed(() => store.getters.isUserOfflineMode);
  const isSyncing = computed(() => store.getters['sync/isSyncing']);
  const hasPendingUpdates = computed(() => store.getters['sync/hasPendingUpdates']);
  const isTourneeDataCached = computed(() => store.getters.isTourneeDataCached);
  const isTourneeCurrentDate = computed(() => store.getters.isTourneeCurrentDate);

  // Computed property pour combiner et trier les deliveries et shop_deliveries
  const combinedDeliveries = computed(() => {
    if (!tourneeData.value) return [];
    
    const regularDeliveries = Array.isArray(tourneeData.value.deliveries) 
      ? tourneeData.value.deliveries.map(delivery => ({...delivery, shop: false}))
      : [];
    
    const shopDeliveries = Array.isArray(tourneeData.value.shop_deliveries)
      ? tourneeData.value.shop_deliveries.map(shopDelivery => {
          // Garder le statut original, ne pas le convertir en majuscules
          return {
            ...shopDelivery,
            shop: true
            // Pas de normalisation du format de statut ici
          };
        })
      : [];
    
    // Combiner et trier par route_position
    return [...regularDeliveries, ...shopDeliveries]
      .sort((a, b) => a.route_position - b.route_position);
  });

  // Déterminer si les données doivent être rechargées
  const shouldLoadData = computed(() => {
    return !tourneeData.value ||
      (!tourneeData.value.deliveries && !tourneeData.value.shop_deliveries) ||
      ((tourneeData.value.deliveries?.length === 0) && (tourneeData.value.shop_deliveries?.length === 0)) ||
      !isTourneeDataCached.value ||
      !isTourneeCurrentDate.value;
  });

  // Vérifier si une livraison spécifique a des mises à jour en attente
  const hasPendingUpdatesForDelivery = (deliveryId) => {
    // Pour les livraisons de type shop, on ne gère pas encore les mises à jour en attente
    const delivery = combinedDeliveries.value.find(d => d.id === deliveryId);
    if (delivery?.shop) {
      return false; // Pour l'instant, on considère qu'il n'y a pas de mises à jour en attente pour les shop_deliveries
    }
    
    // Utiliser la fonction existante pour les livraisons régulières
    return store.getters['sync/hasPendingUpdatesForDelivery'](deliveryId);
  };

  // Mise à jour du compteur des modifications en attente et vérification des erreurs
  const updatePendingCount = async () => {
    try {
      const pendingData = await syncService.exportPendingData();
      pendingUpdatesCount.value = pendingData.updates.length + pendingData.images.length;
      
      // Vérifier s'il y a des mises à jour nécessitant une intervention manuelle
      await checkManualSyncItems();
    } catch (error) {
      console.error('Erreur lors de la récupération du nombre de mises à jour en attente:', error);
      pendingUpdatesCount.value = 0;
    }
  };

  // Vérifier les mises à jour qui nécessitent une intervention manuelle
  const checkManualSyncItems = async () => {
    try {
      hasManualSyncNeeded.value = await store.getters['sync/hasManualSyncItems']();
    } catch (error) {
      console.error('Erreur lors de la vérification des items de synchronisation manuelle:', error);
      hasManualSyncNeeded.value = false;
    }
  };

  // Chargement des données de tournée
  const loadTourneeData = async () => {
    const timeId = 'loadTourneeData-' + Date.now();
    console.time(timeId);
    const loadingTimeout = setTimeout(() => {
      loading.value = false;
      loadError.value = new Error("Temps de chargement trop long");
      toast.add({
        severity: 'warn',
        summary: 'Chargement incomplet',
        detail: 'Le chargement des données prend plus de temps que prévu. Affichage des données disponibles.',
        life: 5000
      });
    }, 10000); // 10 secondes maximum

    try {
      loading.value = true;
      loadError.value = null;

      // Gérer le mode hors ligne explicitement
      if (!isOnline.value || isUserOfflineMode.value) {
        console.log('Chargement des données en mode hors ligne (cache local)');
        
        // Essayer de récupérer la tournée depuis le cache local via dataService
        try {
          const cachedTournee = await dataService.getTournee('current');
          if (cachedTournee && (cachedTournee.deliveries?.length > 0 || cachedTournee.shop_deliveries?.length > 0)) {
            console.log('Données de tournée trouvées dans le cache local');
            
            // Récupérer explicitement les mises à jour en attente
            const pendingData = await syncService.exportPendingData();
            console.log('Mises à jour en attente trouvées:', pendingData.updates.length);
            
            // Créer une copie profonde des données du cache
            const updatedTournee = JSON.parse(JSON.stringify(cachedTournee));
            let hasUpdates = false;
            
            // Appliquer les mises à jour en attente aux données du cache
            if (pendingData && pendingData.updates && pendingData.updates.length > 0) {
              console.log('Application des mises à jour en attente aux données du cache...');
              
              // Pour chaque mise à jour en attente
              for (const update of pendingData.updates) {
                if (update.deliveryId && update.status) {
                  const deliveryId = update.deliveryId;
                  // Vérifier d'abord dans les livraisons régulières
                  if (updatedTournee.deliveries) {
                    const deliveryIndex = updatedTournee.deliveries.findIndex(d => 
                      d.id === deliveryId || 
                      d.id === Number(deliveryId) || 
                      String(d.id) === deliveryId
                    );
                    
                    if (deliveryIndex !== -1) {
                      console.log(`Application de la mise à jour pour la livraison ${deliveryId}: ${update.status}`);
                      
                      // Mettre à jour le statut et la raison si présente
                      updatedTournee.deliveries[deliveryIndex].status = update.status;
                      if (update.undoneReason) {
                        updatedTournee.deliveries[deliveryIndex].undone_reason = update.undoneReason;
                      }
                      
                      // Mettre à jour également d'autres attributs si présents
                      if (update.cageots_rendus !== undefined) {
                        updatedTournee.deliveries[deliveryIndex].cageots_rendus = update.cageots_rendus;
                      }
                      if (update.consignes_rendues_v2 !== undefined) {
                        updatedTournee.deliveries[deliveryIndex].consignes_rendues_v2 = update.consignes_rendues_v2;
                      }
                      
                      hasUpdates = true;
                      continue; // Passer à la mise à jour suivante
                    }
                  }
                  
                  // Si non trouvé dans les livraisons régulières, vérifier dans les shop_deliveries
                  if (updatedTournee.shop_deliveries) {
                    const shopDeliveryIndex = updatedTournee.shop_deliveries.findIndex(d => 
                      d.id === deliveryId || 
                      d.id === Number(deliveryId) || 
                      String(d.id) === deliveryId
                    );
                    
                    if (shopDeliveryIndex !== -1) {
                      console.log(`Application de la mise à jour pour la livraison shop ${deliveryId}: ${update.status}`);
                      
                      // Mettre à jour le statut 
                      updatedTournee.shop_deliveries[shopDeliveryIndex].status = update.status;
                      
                      hasUpdates = true;
                    } else {
                      console.warn(`Livraison ${deliveryId} non trouvée dans les données du cache`);
                    }
                  }
                }
              }
            }
            
            // Mettre à jour le store Vuex avec les données mises à jour
            store.commit('SET_TOURNEE_DATA', updatedTournee);
            store.commit('SET_LAST_TOURNEE_UPDATE', Date.now());
            
            // Log résultat pour aider au debugging
            if (hasUpdates) {
              console.log('Données de tournée mises à jour avec les modifications en attente');
            } else {
              console.log('Aucune modification appliquée aux données de tournée du cache');
            }
            
            // Afficher un message indiquant que les données sont chargées depuis le cache
            toast.add({
              severity: 'info',
              summary: 'Mode hors ligne',
              detail: 'Affichage des données disponibles en cache local',
              life: 3000
            });
            
            clearTimeout(loadingTimeout);
            loading.value = false;
          } else {
            clearTimeout(loadingTimeout);
            loading.value = false;
            toast.add({
              severity: 'warn',
              summary: 'Mode hors ligne',
              detail: 'Aucune donnée de tournée disponible en cache local',
              life: 3000
            });
          }
        } catch (cacheError) {
          console.error('Erreur lors de la récupération depuis le cache:', cacheError);
          clearTimeout(loadingTimeout);
          loadError.value = new Error("Impossible de charger les données du cache");
          loading.value = false;
          
          toast.add({
            severity: 'error',
            summary: 'Erreur',
            detail: 'Impossible de charger les données depuis le cache local',
            life: 3000
          });
        }
      } else {
        // Mode en ligne - Appel sans ID de tournée, le backend récupère la tournée du jour pour l'utilisateur courant
        try {
        const result = await store.dispatch('loadTourneeData');
          
          // Mettre à jour le cache même si on est en ligne pour assurer la disponibilité hors-ligne future
          if (result && (result.deliveries?.length > 0 || result.shop_deliveries?.length > 0)) {
            // Stocker explicitement la tournée complète dans le cache
            await dataService.saveTournee('current', result);
            console.log('Tournée mise en cache avec succès');
            
            // Précharger explicitement chaque livraison dans le cache pour un accès rapide
            if (result.deliveries?.length > 0) {
              for (const delivery of result.deliveries) {
                await syncService.cacheDelivery(delivery.id, delivery);
              }
            }
            
            // Précharger aussi les shop_deliveries s'il y en a
            if (result.shop_deliveries?.length > 0) {
              console.log('Mise en cache de', result.shop_deliveries.length, 'livraisons shop');
              for (const shopDelivery of result.shop_deliveries) {
                await syncService.cacheDelivery(shopDelivery.id, { ...shopDelivery, shop: true });
              }
            }
          }
        
          // Nettoyage du timeout si le chargement réussit
          clearTimeout(loadingTimeout);
          loading.value = false;

          if (!result) {
            toast.add({
              severity: 'info',
              summary: 'Information',
              detail: 'Aucune tournée disponible pour aujourd\'hui',
              life: 3000
            });
          }
        } catch (serverError) {
          console.error('Erreur lors du chargement depuis le serveur:', serverError);
          
          // En cas d'erreur serveur, essayer de charger depuis le cache comme fallback
          try {
            const cachedTournee = await dataService.getTournee('current');
            if (cachedTournee && (cachedTournee.deliveries?.length > 0 || cachedTournee.shop_deliveries?.length > 0)) {
              console.log('Fallback: Utilisation des données en cache après échec du serveur');
              store.commit('SET_TOURNEE_DATA', cachedTournee);
              store.commit('SET_LAST_TOURNEE_UPDATE', Date.now());
              
              toast.add({
                severity: 'warn',
                summary: 'Problème de connexion',
                detail: 'Utilisation des données en cache suite à un problème de communication avec le serveur',
                life: 3000
              });
            } else {
              throw new Error('Aucune donnée en cache disponible');
            }
          } catch (fallbackError) {
            console.error('Erreur lors du fallback vers le cache:', fallbackError);
            toast.add({
              severity: 'error',
              summary: 'Erreur',
              detail: 'Impossible de charger les données de la tournée (serveur ou cache)',
              life: 5000
            });
          } finally {
            clearTimeout(loadingTimeout);
            loading.value = false;
          }
        }
      }
      
      // Mise à jour du compteur des modifications en attente
      await updatePendingCount();
      
    } catch (error) {
      clearTimeout(loadingTimeout);
      console.error('Erreur lors du chargement de la tournée:', error);
      loading.value = false;
      
      // Message d'erreur plus spécifique pour le mode hors ligne
      if (!isOnline.value || isUserOfflineMode.value) {
        toast.add({
          severity: 'error',
          summary: 'Erreur en mode hors ligne',
          detail: 'Impossible de charger les données depuis le cache local. Vérifiez que vous avez déjà utilisé l\'application en ligne au moins une fois.',
          life: 5000
        });
      } else {
        toast.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de charger les données de la tournée depuis le serveur',
          life: 5000
        });
      }
    } finally {
      console.timeEnd(timeId);
    }
  };

  // Fonction pour récupérer et appliquer les statuts des livraisons avec des mises à jour en attente
  const applyPendingStatusUpdates = async () => {
    if (!tourneeData.value) return;
    
    try {
      console.log('Recherche et application des mises à jour en attente...');
      const pendingData = await syncService.exportPendingData();
      const pendingUpdates = pendingData.updates.filter(update => !update.synced);
      
      if (pendingUpdates.length === 0) {
        console.log('Aucune mise à jour en attente trouvée');
        return;
      }
      
      console.log(`${pendingUpdates.length} mises à jour en attente trouvées, application locale...`);
      
      // Si tourneeData n'a pas encore de livraisons, charger complètement depuis le cache
      if (!tourneeData.value.deliveries && !tourneeData.value.shop_deliveries) {
        console.log('Données de tournée incomplètes, rechargement depuis le cache...');
        await loadTourneeData();
        // Vérifier à nouveau si nous avons des livraisons après le chargement
        if (!tourneeData.value || (!tourneeData.value.deliveries && !tourneeData.value.shop_deliveries)) {
          console.log('Impossible de charger les données de livraison depuis le cache');
          return;
        }
      }
      
      // Créer des copies profondes des listes de livraisons
      const updatedDeliveries = JSON.parse(JSON.stringify(tourneeData.value.deliveries || []));
      const updatedShopDeliveries = JSON.parse(JSON.stringify(tourneeData.value.shop_deliveries || []));
      let hasUpdates = false;
      
      // Pour chaque mise à jour en attente
      for (const update of pendingUpdates) {
        if (!update.deliveryId || !update.status) continue;
        
        // Chercher la livraison correspondante dans les livraisons régulières
        let deliveryIndex = updatedDeliveries.findIndex(d => 
          d.id === update.deliveryId || 
          d.id === Number(update.deliveryId) || 
          String(d.id) === update.deliveryId
        );
        
        if (deliveryIndex !== -1) {
          console.log(`Application du statut "${update.status}" à la livraison ${update.deliveryId} (précédemment: ${updatedDeliveries[deliveryIndex].status})`);
          
          // Appliquer les modifications
          updatedDeliveries[deliveryIndex] = {
            ...updatedDeliveries[deliveryIndex],
            status: update.status,
            undone_reason: update.undoneReason
          };
          
          hasUpdates = true;
        } else {
          // Chercher dans les shop_deliveries
          const shopDeliveryIndex = updatedShopDeliveries.findIndex(d => 
            d.id === update.deliveryId || 
            d.id === Number(update.deliveryId) || 
            String(d.id) === update.deliveryId
          );
          
          if (shopDeliveryIndex !== -1) {
            // Normaliser le statut en minuscules pour les shop_deliveries
            const normalizedStatus = typeof update.status === 'string' ? update.status.toLowerCase() : update.status;
            
            console.log(`Application du statut "${normalizedStatus}" à la livraison shop ${update.deliveryId} (précédemment: ${updatedShopDeliveries[shopDeliveryIndex].status})`);
            
            // Appliquer les modifications
            updatedShopDeliveries[shopDeliveryIndex] = {
              ...updatedShopDeliveries[shopDeliveryIndex],
              status: normalizedStatus
              // Note: les shop_deliveries pourraient avoir des champs différents pour les raisons d'échec
            };
            
            hasUpdates = true;
          }
        }
      }
      
      // Si des mises à jour ont été appliquées, mettre à jour la tournée
      if (hasUpdates) {
        console.log('Application des mises à jour locales à la tournée');
        
        // Ne pas modifier directement tourneeData.value (computed property)
        // Utiliser une mutation Vuex à la place
        store.commit('SET_TOURNEE_DATA', {
          ...tourneeData.value,
          deliveries: updatedDeliveries,
          shop_deliveries: updatedShopDeliveries
        });
      }
    } catch (error) {
      console.error('Erreur lors de l\'application des mises à jour en attente:', error);
    }
  };

  // Fonction pour mettre à jour explicitement le statut d'une shop_delivery dans les données combinées
  const updateShopDeliveryStatus = (deliveryId, newStatus) => {
    if (!tourneeData.value || !tourneeData.value.shop_deliveries) return false;
    
    console.log(`Tentative de mise à jour manuelle du statut de la shop_delivery ${deliveryId} vers "${newStatus}"`);
    
    // Chercher la shop_delivery dans les données de la tournée
    const shopDeliveryIndex = tourneeData.value.shop_deliveries.findIndex(d => 
      d.id === deliveryId || 
      d.id === Number(deliveryId) || 
      String(d.id) === String(deliveryId)
    );
    
    if (shopDeliveryIndex !== -1) {
      console.log(`Shop delivery ${deliveryId} trouvée, mise à jour du statut de "${tourneeData.value.shop_deliveries[shopDeliveryIndex].status}" vers "${newStatus}"`);
      
      // Créer une copie des données pour éviter de modifier directement le state
      const updatedShopDeliveries = [...tourneeData.value.shop_deliveries];
      
      // Mettre à jour le statut
      updatedShopDeliveries[shopDeliveryIndex] = {
        ...updatedShopDeliveries[shopDeliveryIndex],
        status: newStatus
      };
      
      // Mettre à jour le store
      store.commit('SET_TOURNEE_DATA', {
        ...tourneeData.value,
        shop_deliveries: updatedShopDeliveries
      });
      
      // Mettre à jour explicitement le cache local
      dataService.saveTournee('current', {
        ...tourneeData.value,
        shop_deliveries: updatedShopDeliveries
      });
      
      return true;
    }
    
    console.log(`Shop delivery ${deliveryId} non trouvée dans les données de la tournée`);
    return false;
  };

  // Exécution initiale
  const initialize = async () => {
    if (shouldLoadData.value) {
      await loadTourneeData();
    } else {
      // Si les données sont déjà en mémoire, appliquer explicitement les mises à jour en attente
      loading.value = false;
      await applyPendingStatusUpdates();
    }
    
    // Mise à jour du compteur de mises à jour en attente
    await updatePendingCount();
  };

  // Mettre en place des watchers pour les changements d'état pertinents
  watch(isOnline, (newValue, oldValue) => {
    if (newValue && !oldValue && shouldLoadData.value && !isUserOfflineMode.value) {
      // Si la connexion est rétablie et les données doivent être rechargées
      loadTourneeData();
    }
  });

  watch(isUserOfflineMode, (newValue) => {
    if (!newValue && shouldLoadData.value && isOnline.value) {
      // Si le mode hors ligne est désactivé et nous sommes en ligne
      loadTourneeData();
    }
  });

  return {
    // État
    loading,
    loadError,
    pendingUpdatesCount,
    hasManualSyncNeeded,
    
    // Propriétés calculées
    tourneeData,
    isOnline,
    isUserOfflineMode,
    isSyncing,
    hasPendingUpdates,
    combinedDeliveries,
    shouldLoadData,
    
    // Méthodes
    loadTourneeData,
    updatePendingCount,
    applyPendingStatusUpdates,
    hasPendingUpdatesForDelivery,
    updateShopDeliveryStatus,
    initialize,
  };
} 